import type { FigureKind, TFigureCaptionElement } from '@alpod-ai-writer/plate-plugins/figure-caption';
import { type DocumentSection_Content, DocumentSectionType, type DocumentTemplate } from '@alpod-ai-writer/shared';
import { yTextToSlateElement } from '@slate-yjs/core';
import { useEffect,useState } from 'react';
import { Element, Node } from 'slate';
import * as Y from 'yjs';

import { isElementWithType } from '../utils/editor.utils';
import { useDocumentEditor } from './useDocumentEditor.hook';

export interface FigureRef {
    sectionId: string;
    sectionTitle: string;
    headingId?: string;
    headingTitle?: string;
    path: number[];
}

export interface FigureItem {
    id: string;
    kind: FigureKind;
    label: string;
    ref: FigureRef;
}

export function useFigureList() {
    const editor = useDocumentEditor();
    const document = editor.yjsProvider.document;

    const [ figures, setFigures ] = useState(getFiguresFromDocument(editor.template, document));

    useEffect(() => {
        let debounceTimer: ReturnType<typeof setTimeout>|null = null;

        const handleUpdate = () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                const newState = getFiguresFromDocument(editor.template, document);
                setFigures(newState);
            }, 300);
        };

        document.on('updateV2', handleUpdate);
        handleUpdate();

        return () => {
            document.off('updateV2', handleUpdate);
            
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [ document ]);

    return { figures };
}

function getFiguresFromDocument(template: DocumentTemplate, document: Y.Doc) {
    const items: FigureItem[] = [];
    let lastHeadingId: string|undefined;
    let lastHeadingLabel: string|undefined;

    const findFigure = (sectionId: string, sectionTitle: string, path: number[], elements: unknown[]) => {
        elements.forEach((element, index) => {
            if (isElementWithType(element)) {
                const _path = [ ...path, index ];

                switch (element.type) {
                    case 'figure-caption': {
                        if (!isElementWithType<TFigureCaptionElement>(element, 'figure-caption')) {
                            break;
                        }

                        const caption = element.children.map(Node.string).join(' ');

                        if (!!caption) {
                            items.push({
                                id: element.id!,
                                kind: element.figureKind,
                                label: caption,
                                ref: {
                                    sectionId,
                                    sectionTitle,
                                    headingId: lastHeadingId,
                                    headingTitle: lastHeadingLabel,
                                    path: _path
                                }
                            });
                        }

                        break;
                    }
                    case 'h1':
                    case 'h2':
                    case 'h3': {
                        lastHeadingId = element.id;
                        lastHeadingLabel = Node.string(element as Element);
                        break;
                    }
                }

                if (element.children?.length > 0) {
                    findFigure(sectionId, sectionTitle, _path, element.children);
                }
            }
        });
    };

    template.config.sections
        .filter((section): section is DocumentSection_Content => section.type === DocumentSectionType.Content)
        .forEach(section => {
            if (!section.content) {
                return;
            }

            const docContent = document.get(section.content.id, Y.XmlText);

            if (docContent instanceof Y.XmlText) {
                const slateContent = yTextToSlateElement(docContent);

                lastHeadingId = undefined;
                lastHeadingLabel = undefined;

                findFigure(section.id, section.title, [], slateContent.children);
            }
        });

    return items;
}
